import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardTitle,
} from '@/components/ui/card'
import { orpc } from '@/lib/orpc/client'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Code } from 'lucide-react'

export const Route = createFileRoute('/_dashboard/settings/integrations/')({
  component: IntegrationsPage,
})

interface Integration {
  id: string
  name: string
  href: string
  description?: string
  icon: React.ComponentType<{ className?: string }> | string
  status: 'connected' | 'disconnected' | 'error'
}

function AvailableIntegrationRow({
  integration,
}: {
  integration: Integration
}) {
  return (
    <div className="flex flex-col sm:flex-row gap-4 justify-between items-start sm:items-center w-full max-w-full p-4">
      <div className="flex flex-row flex-1 items-center gap-4">
        <div className="flex flex-row items-center justify-between gap-4">
          {typeof integration.icon === 'string' ? (
            <div className="flex items-center justify-center h-10! w-10! rounded-lg bg-gradient-to-br">
              <img
                src={integration.icon}
                alt={integration.name}
                className="w-full h-full object-contain"
              />
            </div>
          ) : (
            <div className="flex items-center justify-center h-10! w-10! rounded-lg bg-gradient-to-br from-blue-200 to-purple-200">
              <integration.icon className="w-6 h-6 text-blue-600" />
            </div>
          )}
        </div>
        <div className="space-y-1">
          <CardTitle>{integration.name}</CardTitle>
          <CardDescription>{integration.description}</CardDescription>
        </div>
      </div>
      <div>
        <div className="flex gap-3 items-center">
          {integration.status === 'connected' && (
            <>
              <Badge variant="success">Active</Badge>
              <Button variant="outline" asChild>
                <Link to={integration.href}>Manage</Link>
              </Button>
            </>
          )}
          {integration.status === 'disconnected' && (
            <>
              <Badge variant="secondary">Inactive</Badge>
              <Button variant="outline" asChild>
                <Link to={integration.href}>Connect</Link>
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function AvailableIntegrations({ className }: { className?: string }) {
  const { data: integrationData } = useQuery(
    orpc.integrations.getIntegrations.queryOptions(),
  )

  const enabledMap =
    integrationData?.reduce(
      (acc, integration) => {
        acc[integration.config.type] = integration.enabled
        return acc
      },
      {} as Record<string, boolean>,
    ) || {}

  const integrations: Integration[] = [
    {
      id: 'custom',
      href: '/settings/integrations/custom',
      name: 'Custom Integration',
      description: 'Create a custom integration with our webhook system.',
      icon: Code,
      status: enabledMap['custom'] ? 'connected' : 'disconnected',
    },
    {
      id: 'framer',
      href: '/settings/integrations/framer',
      name: 'Framer',
      icon: '/framer-logo.svg',
      status: enabledMap['framer'] ? 'connected' : 'disconnected',
    },
    // {
    //   id: 'webflow',
    //   href: '/settings/integrations/webflow',
    //   name: 'Webflow',
    //   icon: '/webflow-logo.png',
    //   status: enabledMap['webflow'] ? 'connected' : 'disconnected',
    // },
    {
      id: 'wix',
      href: '/settings/integrations/wix',
      name: 'Wix',
      icon: '/integrations/wix-logo.png',
      status: enabledMap['wix'] ? 'connected' : 'disconnected',
    },
    {
      id: 'wordpress',
      href: '/settings/integrations/wordpress',
      name: 'WordPress',
      icon: '/wordpresscom-logo.webp',
      status: enabledMap['wordpress'] ? 'connected' : 'disconnected',
    },
    {
      id: 'shopify',
      href: '/settings/integrations/shopify',
      name: 'Shopify',
      icon: '/shopify-logo.webp',
      status: enabledMap['shopify'] ? 'connected' : 'disconnected',
    },
  ]
  return (
    <div className={className}>
      <div
        className="gap-3 w-full bg-card rounded-3xl border-border border"
        style={{ scrollbarColor: 'gray transparent' }}
      >
        {integrations.map((integration) => {
          return (
            <>
              <AvailableIntegrationRow
                key={integration.id}
                integration={integration}
              />
              <div className="h-px w-full bg-border last:hidden" />
            </>
          )
        })}
      </div>
    </div>
  )
}

function IntegrationsPage() {
  return (
    <div className="w-full">
      <AvailableIntegrations className="mb-8" />

      {/* Custom Integration Info */}
      <Card className="mt-8 bg-blue-50 border-blue-200">
        <CardContent>
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-100 rounded-lg">
              <Code className="w-6 h-6 text-blue-600" />
            </div>
            <div className="flex-1">
              <CardTitle className="text-lg">Need more help?</CardTitle>
              <CardDescription className="text-gray-600">
                Don't see your platform? Need help with a custom integration?
                Contact us for support.
                <br />
                <span>support@userocketrank.com</span>
              </CardDescription>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
